import React, { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import client, { apiError, API } from "@/lib/api";
import { toast } from "sonner";
import { PageHeader, Loading } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Upload, FileSpreadsheet, ClipboardPaste, ArrowLeft, ArrowRight, CheckCircle2, AlertTriangle, Copy } from "lucide-react";

const FIELDS = [
  { value: "full_name", label: "Full Name" }, { value: "dob", label: "Date of Birth" },
  { value: "category", label: "Category" }, { value: "parent_phone", label: "Parent Phone" },
  { value: "parent_name", label: "Parent Name" }, { value: "city", label: "City" },
  { value: "position", label: "Position" }, { value: "ignore", label: "— Ignore —" },
];

export default function ImportPlayers() {
  const nav = useNavigate();
  const fileRef = useRef();
  const [step, setStep] = useState(1);
  const [text, setText] = useState("");
  const [columns, setColumns] = useState([]);
  const [rows, setRows] = useState([]);
  const [mapping, setMapping] = useState({});
  const [validation, setValidation] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [skipDup, setSkipDup] = useState(true);

  const handleParse = (data) => { setColumns(data.columns); setRows(data.rows); setMapping(data.mapping); setStep(2); };

  const parseText = async () => {
    if (!text.trim()) { toast.error("Paste some data first"); return; }
    setLoading(true);
    try { const r = await client.post("/players/import/parse-text", { text }); handleParse(r.data); }
    catch (e) { toast.error(apiError(e)); } finally { setLoading(false); }
  };
  const parseFile = async (file) => {
    if (!file) return;
    setLoading(true);
    const fd = new FormData(); fd.append("file", file);
    try { const r = await client.post("/players/import/parse-file", fd, { headers: { "Content-Type": "multipart/form-data" } }); handleParse(r.data); }
    catch (e) { toast.error(apiError(e)); } finally { setLoading(false); }
  };
  const validate = async () => {
    setLoading(true);
    try { const r = await client.post("/players/import/validate", { rows, mapping }); setValidation(r.data); setStep(3); }
    catch (e) { toast.error(apiError(e)); } finally { setLoading(false); }
  };
  const commit = async () => {
    setLoading(true);
    const records = validation.results.filter((r) => !r.errors.length && !(skipDup && r.duplicate)).map((r) => r.record);
    try { const r = await client.post("/players/import/commit", { records, skip_duplicates: skipDup }); setResult(r.data); setStep(4); }
    catch (e) { toast.error(apiError(e)); } finally { setLoading(false); }
  };

  const STEPS = ["Upload / Paste", "Map Columns", "Validate", "Done"];

  return (
    <div>
      <Button variant="ghost" onClick={() => nav("/players")} className="mb-2"><ArrowLeft size={16} className="me-1" /> Back</Button>
      <PageHeader title="Import Players" subtitle="Bulk import from Excel, CSV, Word or paste" testid="import-page" />

      <div className="flex items-center gap-2 mb-6">
        {STEPS.map((s, i) => (
          <React.Fragment key={s}>
            <div className={`flex items-center gap-2 ${step >= i + 1 ? "text-emerald-600" : "text-muted-foreground"}`}>
              <div className={`h-7 w-7 rounded-full flex items-center justify-center text-xs font-bold ${step >= i + 1 ? "bg-emerald-600 text-white" : "bg-secondary"}`}>{i + 1}</div>
              <span className="text-sm font-medium hidden sm:block">{s}</span>
            </div>
            {i < STEPS.length - 1 && <div className="flex-1 h-px bg-border" />}
          </React.Fragment>
        ))}
      </div>

      {loading && <Loading />}

      {!loading && step === 1 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card className="p-6">
            <h3 className="font-bold font-heading mb-3 flex items-center gap-2"><FileSpreadsheet size={18} className="text-emerald-600" /> Upload File</h3>
            <input ref={fileRef} type="file" accept=".xlsx,.xls,.csv,.docx" className="hidden" onChange={(e) => parseFile(e.target.files[0])} data-testid="import-file-input" />
            <div onClick={() => fileRef.current?.click()} className="border-2 border-dashed border-border rounded-xl p-10 text-center cursor-pointer hover:border-emerald-500 hover:bg-emerald-500/5 transition-colors" data-testid="import-dropzone">
              <Upload className="mx-auto mb-2 text-muted-foreground" size={32} />
              <p className="font-medium">Click to upload</p>
              <p className="text-sm text-muted-foreground mt-1">Excel (.xlsx), CSV or Word (.docx)</p>
            </div>
          </Card>
          <Card className="p-6">
            <h3 className="font-bold font-heading mb-3 flex items-center gap-2"><ClipboardPaste size={18} className="text-emerald-600" /> Paste Data</h3>
            <p className="text-xs text-muted-foreground mb-2">One player per line. Separate fields with | , tab or comma.<br />e.g. <code>Mohamed Amine | 14/11/2014</code></p>
            <Textarea value={text} onChange={(e) => setText(e.target.value)} rows={8} placeholder={"Mohamed Amine | 14/11/2014\nYassine Touzani | 22/03/2015"} data-testid="import-paste" />
            <Button className="w-full mt-3 bg-emerald-600 hover:bg-emerald-700 text-white" onClick={parseText} data-testid="import-parse-text">Continue <ArrowRight size={16} className="ms-1" /></Button>
          </Card>
        </div>
      )}

      {!loading && step === 2 && (
        <Card className="p-6">
          <h3 className="font-bold font-heading mb-4">Map Columns</h3>
          <p className="text-sm text-muted-foreground mb-4">Tell us which column maps to which field. We auto-detected some.</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
            {columns.map((c, i) => (
              <div key={i} className="p-3 rounded-lg border border-border">
                <p className="text-xs font-bold uppercase text-muted-foreground mb-2">{c}</p>
                <Select value={mapping[String(i)] || "ignore"} onValueChange={(v) => setMapping({ ...mapping, [String(i)]: v })}>
                  <SelectTrigger data-testid={`map-col-${i}`}><SelectValue /></SelectTrigger>
                  <SelectContent>{FIELDS.map((f) => <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            ))}
          </div>
          <div className="overflow-x-auto scrollbar-thin border border-border rounded-lg mb-4">
            <table className="w-full text-sm">
              <thead className="bg-muted/50"><tr>{columns.map((c, i) => <th key={i} className="text-start px-3 py-2 text-xs font-semibold">{mapping[String(i)] && mapping[String(i)] !== "ignore" ? FIELDS.find((f) => f.value === mapping[String(i)])?.label : c}</th>)}</tr></thead>
              <tbody className="divide-y divide-border">
                {rows.slice(0, 5).map((r, ri) => <tr key={ri}>{r.map((c, ci) => <td key={ci} className="px-3 py-2">{c}</td>)}</tr>)}
              </tbody>
            </table>
          </div>
          <p className="text-xs text-muted-foreground mb-4">Showing {Math.min(5, rows.length)} of {rows.length} rows</p>
          <div className="flex justify-between">
            <Button variant="outline" onClick={() => setStep(1)}><ArrowLeft size={16} className="me-1" /> Back</Button>
            <Button className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={validate} data-testid="import-validate">Validate <ArrowRight size={16} className="ms-1" /></Button>
          </div>
        </Card>
      )}

      {!loading && step === 3 && validation && (
        <Card className="p-6">
          <h3 className="font-bold font-heading mb-4">Validation & Duplicates</h3>
          <div className="grid grid-cols-3 gap-4 mb-4">
            <div className="p-4 rounded-lg bg-emerald-500/10 text-center"><p className="text-2xl font-extrabold text-emerald-600">{validation.valid}</p><p className="text-xs uppercase font-bold text-muted-foreground">Valid</p></div>
            <div className="p-4 rounded-lg bg-amber-500/10 text-center"><p className="text-2xl font-extrabold text-amber-600">{validation.duplicates}</p><p className="text-xs uppercase font-bold text-muted-foreground">Duplicates</p></div>
            <div className="p-4 rounded-lg bg-rose-500/10 text-center"><p className="text-2xl font-extrabold text-rose-600">{validation.results.filter((r) => r.errors.length).length}</p><p className="text-xs uppercase font-bold text-muted-foreground">Errors</p></div>
          </div>
          <label className="flex items-center gap-2 mb-4 text-sm"><input type="checkbox" checked={skipDup} onChange={(e) => setSkipDup(e.target.checked)} data-testid="skip-dup" /> Skip duplicate players</label>
          <div className="overflow-x-auto scrollbar-thin border border-border rounded-lg mb-4 max-h-96">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 sticky top-0"><tr>{["#", "Name", "DOB", "Age", "Category", "Status"].map((h) => <th key={h} className="text-start px-3 py-2 text-xs font-semibold">{h}</th>)}</tr></thead>
              <tbody className="divide-y divide-border">
                {validation.results.map((r) => (
                  <tr key={r.index} data-testid={`validate-row-${r.index}`} className={r.errors.length ? "bg-rose-500/5" : r.duplicate ? "bg-amber-500/5" : ""}>
                    <td className="px-3 py-2 text-muted-foreground">{r.index + 1}</td>
                    <td className="px-3 py-2 font-medium">{r.record.full_name || <span className="text-rose-600">—</span>}</td>
                    <td className="px-3 py-2">{r.record.dob || "—"}</td>
                    <td className="px-3 py-2">{r.record.age ?? "—"}</td>
                    <td className="px-3 py-2">{r.record.category || "—"}</td>
                    <td className="px-3 py-2">
                      {r.errors.length ? <span className="text-rose-600 text-xs flex items-center gap-1"><AlertTriangle size={13} /> {r.errors.join(", ")}</span>
                        : r.duplicate ? <span className="text-amber-600 text-xs flex items-center gap-1"><Copy size={13} /> {r.duplicate_reason}</span>
                          : <span className="text-emerald-600 text-xs flex items-center gap-1"><CheckCircle2 size={13} /> OK</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex justify-between">
            <Button variant="outline" onClick={() => setStep(2)}><ArrowLeft size={16} className="me-1" /> Back</Button>
            <Button className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={commit} data-testid="import-commit">Import {validation.valid - (skipDup ? validation.duplicates : 0)} Players</Button>
          </div>
        </Card>
      )}

      {!loading && step === 4 && result && (
        <Card className="p-12 text-center">
          <CheckCircle2 className="mx-auto text-emerald-600 mb-4" size={56} />
          <h3 className="text-2xl font-extrabold font-heading">{result.created} players imported successfully!</h3>
          <p className="text-muted-foreground mt-2">Ages and categories were calculated automatically and players sorted.</p>
          <div className="flex gap-3 justify-center mt-6">
            <Button variant="outline" onClick={() => { setStep(1); setText(""); setValidation(null); setResult(null); }}>Import More</Button>
            <Button className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => nav("/players")} data-testid="import-done">View Players</Button>
          </div>
        </Card>
      )}
    </div>
  );
}
